// Homepage hero slider: a native scroll-snap track with prev/next/go/toggle commands and gentle autoplay.
class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-slider-track]');
    this.slides = this.track ? [...this.track.children] : [];
    this.dots = [...this.querySelectorAll('[data-slider-dot]')];
    this.toggle = this.querySelector('[data-slider-toggle]');
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 7000;
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.playing = !this.reducedMotion && this.slides.length > 1;
    this.hovered = false;
    if (this.slides.length < 2) return;

    this.addEventListener('command', (event) => this.onCommand(event), { signal });
    this.track.addEventListener('scroll', () => this.onScroll(), { signal, passive: true });
    // Pause while the visitor is reading or using the slide, then resume.
    this.addEventListener('pointerenter', () => { this.hovered = true; this.render(); }, { signal });
    this.addEventListener('pointerleave', () => { this.hovered = false; this.render(); }, { signal });
    this.addEventListener('focusin', () => { this.hovered = true; this.render(); }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) { this.hovered = false; this.render(); }
    }, { signal });
    document.addEventListener('visibilitychange', () => this.render(), { signal });

    this.render();
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearInterval(this.timer);
  }

  onCommand(event) {
    if (event.command === '--next') this.go(this.index + 1);
    else if (event.command === '--prev') this.go(this.index - 1);
    else if (event.command === '--go') this.go(Number(event.source?.dataset.index) || 0);
    else if (event.command === '--toggle') { this.playing = !this.playing; this.render(); }
  }

  onScroll() {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      const width = this.track.clientWidth || 1;
      const next = Math.round(Math.abs(this.track.scrollLeft) / width);
      if (next !== this.index) { this.index = next; this.render(); }
    }, 80);
  }

  go(target) {
    const count = this.slides.length;
    this.index = (target + count) % count;
    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: direction * this.index * this.track.clientWidth, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    this.render();
  }

  render() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.classList.toggle('bg-accent', active);
      dot.classList.toggle('bg-border', !active);
    });
    if (this.toggle) {
      this.toggle.setAttribute('aria-pressed', String(!this.playing));
      this.toggle.textContent = this.playing ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel;
    }
    // Announce slide changes only when the visitor is in control, not during autoplay.
    this.track.setAttribute('aria-live', this.playing ? 'off' : 'polite');

    clearInterval(this.timer);
    if (this.playing && !this.hovered && !document.hidden) {
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }
  }
}

if (!customElements.get('s-hero-slider')) customElements.define('s-hero-slider', HeroSlider);
